// PublicBooking — patient-facing booking widget served at book.doctrue.in.
//
// Visual language mirrors the admin AppointmentBooking screen so that
// hospitals see a consistent product, but the flow is meaningfully
// different:
//   - Unauthenticated. There's no userData / hospital context — only the
//     hospital_id parsed from the URL drives every API call.
//   - OTP-gated. A WhatsApp OTP step sits between "Book Appointment" and
//     the actual /patient + /booking calls when the hospital opts in via
//     the public_booking_otp_required flag (default true / fail-secure).
//   - No walk-ins. Only "booking" mode (no walk-in / video) — see notes
//     on the BookingType type below.
//
// Key memories used here (see ~/.claude memory directory):
//   - project_dow_numbering        (day_of_week is Sun=1..Sat=7)
//   - project_express_validator_optional (omit null queue-type fields)
//   - project_public_booking_otp_flag    (OTP feature flag semantics)
//   - project_public_booking_auth_redirect (UserContext guard for /?public=1)
//   - project_public_booking_device_trust (10-day OTP-skip cookie window)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment-timezone";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { FiUser, FiSearch, FiChevronRight, FiX } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import PhoneNumberSelect from "../components/atoms/PhoneNumberSelect";
import {
  getPublicHospital,
  getPublicFeatureFlags,
  getPublicOnlineDoctors,
  getPublicDoctorAvailability,
  getPublicSlots,
  getPublicTokens,
  searchPublicPatient,
  createPublicPatient,
  createPublicBooking,
  sendBookingOtp,
  verifyBookingOtp,
  HospitalBranding,
  PublicFeatureFlags,
  PublicDoctor,
  PublicDoctorAvailability,
  PublicSlot,
  PublicToken,
} from "../lib/apis/publicBooking";

const todayStr = () => moment().format("YYYY-MM-DD");

type Gender = "Male" | "Female";
// PublicBooking only supports physical online bookings — walk-ins happen
// at the front desk via the admin AppointmentBooking flow, not from the
// patient-facing widget. Visit type is the patient's only mode toggle.
type VisitType = "New" | "Follow Up";

const PublicBooking: React.FC = () => {
  const { hospital_id } = useParams<{ hospital_id: string }>();

  // ── Hospital context ─────────────────────────────────────────────
  // Branding + feature flags both come from public endpoints keyed by
  // hospital_id. We fetch them in parallel on mount.
  const [branding, setBranding] = useState<HospitalBranding | null>(null);
  const [flags, setFlags] = useState<PublicFeatureFlags | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [initialError, setInitialError] = useState<string | null>(null);
  // Tripped when any public-route call returns 403 "Public booking is
  // currently disabled" mid-session (the hospital flipped the master
  // kill-switch after widget load). Renders the same unavailable screen
  // as the flag-on-load case.
  const [forcedDisabled, setForcedDisabled] = useState(false);

  // ── Patient form state ───────────────────────────────────────────
  // patientId is filled either by the pre-form lookup (OTP-disabled
  // hospitals) or inside runBookingFlow after verify (OTP-required).
  const [countryCode, setCountryCode] = useState("91");
  const [phone, setPhone] = useState("");
  const [fullName, setFullName] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState<Gender | null>(null);
  const [patientId, setPatientId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // OTP gate — booking_token is short-lived (10 min). Held in-memory only:
  // backend explicitly forbade persistence to localStorage/sessionStorage/cookies.
  // Only used when the hospital's `public_booking_otp_required` flag is true
  // (the default — fail-secure).
  const [otpOpen, setOtpOpen] = useState(false);
  const [bookingToken, setBookingToken] = useState<string | null>(null);

  const [visitType, setVisitType] = useState<VisitType>("New");
  // Free-form comments — optional. Mirrors admin AppointmentBooking's
  // textarea (formState.comments) and feeds the `comments` field of the
  // booking payload. Trim on submit but accept whitespace mid-edit so
  // the cursor doesn't fight the user.
  const [comments, setComments] = useState("");
  // Marketing-communications opt-in — defaults to FALSE per DPDP/GDPR
  // (pre-ticked boxes don't count as consent). NOT gating Submit, by
  // user pref — soft consent capture only. When true, runBookingFlow
  // includes `marketing_consent: true` on the patient create call and
  // backend stamps marketing_consent_at as the audit trail. Note: this
  // is opt-in for promotional WhatsApp/SMS only — it is NOT a Terms-of-
  // Service or Privacy-Policy acceptance (those would need a separate
  // backend column; see project_public_booking_marketing_consent memory).
  const [marketingConsent, setMarketingConsent] = useState(false);

  // ── Appointment selection state ──────────────────────────────────
  // mappingId is the (doctor × hospital) row id, NOT the doctor_id —
  // availability queries are keyed by mapping_id. The doctor_id used
  // in the final booking payload comes from the selected doctor row.
  const [appointmentDate, setAppointmentDate] = useState<string>(todayStr());
  const [doctors, setDoctors] = useState<PublicDoctor[]>([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [mappingId, setMappingId] = useState<string | null>(null);
  const [doctorPickerOpen, setDoctorPickerOpen] = useState(false);
  const [doctorSearch, setDoctorSearch] = useState("");

  const [availabilities, setAvailabilities] = useState<
    PublicDoctorAvailability[]
  >([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [selectedAvailabilityId, setSelectedAvailabilityId] = useState<
    string | null
  >(null);

  const [slots, setSlots] = useState<PublicSlot[]>([]);
  const [tokens, setTokens] = useState<PublicToken[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<PublicSlot | null>(null);
  const [selectedToken, setSelectedToken] = useState<PublicToken | null>(null);
  const [slotTokenLoading, setSlotTokenLoading] = useState(false);
  const [slotTokenKind, setSlotTokenKind] = useState<
    "slots" | "tokens" | "session" | null
  >(null);

  const selectedDoctor = useMemo(
    () => doctors.find((d) => d.mapping_id === mappingId) || null,
    [doctors, mappingId]
  );

  // OTP gate is per-hospital. Default true (fail-secure) — only an explicit
  // false from the flags response disables the OTP flow. When disabled, the
  // widget runs the old Phase-1 behavior: no OTP modal, no Bearer headers,
  // debounced pre-form patient search re-enabled.
  const otpRequired = flags?.public_booking_otp_required !== false;

  // Master kill-switch. Renders the unavailable screen and blocks the form.
  // null/undefined on the flag is treated as "enabled" so widgets keep
  // working when the backend hasn't shipped the flag yet — only an explicit
  // false (or a 403 mid-session) trips it.
  const publicDisabled =
    flags?.public_booking_enabled === false || forcedDisabled;

  // Date picker upper bound: today + public_max_advance_days. null on the
  // flag means "no max — accept anything." Default behaviour (when no flag
  // is returned at all) is also no max; backend will still 400 anything
  // out of its own range and we surface that fallback toast.
  const maxAppointmentDate = useMemo(() => {
    const n = flags?.public_max_advance_days;
    if (n == null) return undefined;
    return moment().add(n, "days").format("YYYY-MM-DD");
  }, [flags?.public_max_advance_days]);

  // Backend's day_of_week is 1-indexed starting Sunday (Sun=1..Sat=7),
  // not JS's 0-indexed Sun=0..Sat=6 — so add 1 to moment().day().
  const sessionsForDate = useMemo(() => {
    if (!appointmentDate || availabilities.length === 0) return [];
    const dow = moment(appointmentDate).day() + 1;
    return availabilities.filter(
      (a) => a.is_active !== false && (a.day_of_week === dow || a.day_of_week == null)
    );
  }, [availabilities, appointmentDate]);

  // Auto-pick the first session for the chosen date when sessions change.
  useEffect(() => {
    if (sessionsForDate.length === 0) {
      setSelectedAvailabilityId(null);
      return;
    }
    setSelectedAvailabilityId((prev) => {
      if (prev && sessionsForDate.some((s) => s.availability_id === prev)) {
        return prev;
      }
      return sessionsForDate[0].availability_id;
    });
  }, [sessionsForDate]);

  // On session change: branch on queue_type.
  // - Slot or Slot_Token  → POST /public/slots
  // - Token               → POST /public/tokens
  // - Session             → no call (the whole session is the booking unit)
  useEffect(() => {
    setSelectedSlot(null);
    setSelectedToken(null);

    if (!mappingId || !selectedAvailabilityId || !appointmentDate) {
      setSlots([]);
      setTokens([]);
      setSlotTokenKind(null);
      return;
    }
    const session = availabilities.find(
      (a) => a.availability_id === selectedAvailabilityId
    );
    if (!session) return;

    if (session.queue_type === "Session") {
      setSlots([]);
      setTokens([]);
      setSlotTokenKind("session");
      return;
    }

    let cancelled = false;
    setSlotTokenLoading(true);
    setSlots([]);
    setTokens([]);

    // Public booking is always a "booking" (vs admin's "walk-in" / "video"):
    // the patient is reserving an upcoming slot online — no walk-in path here.
    const payload = {
      mapping_id: mappingId,
      availability_id: selectedAvailabilityId,
      booked_date: appointmentDate,
      start_time: session.start_time,
      end_time: session.end_time,
      number_of_people: 1,
      bookingType: "booking",
    } as const;

    const isToken = session.queue_type === "Token";
    const kind: "slots" | "tokens" = isToken ? "tokens" : "slots";
    setSlotTokenKind(kind);

    if (isToken) {
      getPublicTokens(payload)
        .then((list) => {
          if (!cancelled) setTokens(list);
        })
        .catch((err) => {
          if (cancelled) return;
          if (isKillSwitchError(err)) {
            setForcedDisabled(true);
            return;
          }
          setTokens([]);
        })
        .finally(() => {
          if (!cancelled) setSlotTokenLoading(false);
        });
    } else {
      getPublicSlots(payload)
        .then((list) => {
          if (!cancelled) setSlots(list);
        })
        .catch((err) => {
          if (cancelled) return;
          if (isKillSwitchError(err)) {
            setForcedDisabled(true);
            return;
          }
          setSlots([]);
        })
        .finally(() => {
          if (!cancelled) setSlotTokenLoading(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [
    mappingId,
    selectedAvailabilityId,
    appointmentDate,
    availabilities,
  ]);

  const resetForm = () => {
    setPhone("");
    setFullName("");
    setAge("");
    setGender(null);
    setVisitType("New");
    setComments("");
    setMarketingConsent(false);
    setSelectedSlot(null);
    setSelectedToken(null);
    setPatientId(null);
    setBookingToken(null);
    // Keep date + selected doctor — common reset behaviour in admin keeps
    // them so the user doesn't have to re-pick after a typo correction.
  };

  // Validate the form; return true if everything is good to go.
  const validateForm = (): boolean => {
    if (!hospital_id) return false;
    if (!fullName.trim()) {
      toast.error("Please enter the full name.");
      return false;
    }
    if (!phone.trim()) {
      toast.error("Please enter the phone number.");
      return false;
    }
    if (countryCode === "91" && phone.length !== 10) {
      toast.error("Phone number must be 10 digits.");
      return false;
    }
    if (!age || Number(age) <= 0) {
      toast.error("Please enter a valid age.");
      return false;
    }
    if (!gender) {
      toast.error("Please select a gender.");
      return false;
    }
    if (!selectedDoctor) {
      toast.error("Please choose a doctor.");
      return false;
    }
    if (!selectedAvailabilityId) {
      toast.error("Please pick a session.");
      return false;
    }
    const session = availabilities.find(
      (a) => a.availability_id === selectedAvailabilityId
    );
    if (!session) {
      toast.error("Session not found, please re-pick.");
      return false;
    }
    if (
      (session.queue_type === "Slot" || session.queue_type === "Slot_Token") &&
      !selectedSlot
    ) {
      toast.error("Please choose a slot timing.");
      return false;
    }
    if (session.queue_type === "Token" && !selectedToken) {
      toast.error("Please choose a token.");
      return false;
    }
    return true;
  };

  // Click "Book Appointment" → validate. If the hospital requires OTP, open
  // the OTP modal (which then drives runBookingFlow on verify). If OTP is
  // disabled for this hospital, run the booking flow directly with no token.
  const handleSubmit = () => {
    if (!validateForm()) return;
    if (otpRequired) {
      setOtpOpen(true);
    } else {
      runBookingFlow(null);
    }
  };

  // Phase 2 + 3 from the canonical spec, run when the user clicks Book.
  //   1. searchPublicPatient — if hit, reuse patient_id; if miss/error, fall
  //      through to create. Both branches mirror the admin path.
  //   2. createPublicPatient — 200/201 are both treated as success per the
  //      backend's dedup contract (200 = existing row was found).
  //   3. createPublicBooking — the actual booking call.
  // On success (or 409 idempotency replay) we toast + resetForm; 401 sends
  // the user back through the OTP modal with the phone field preserved.
  // `token` is the booking_token JWT issued by /verify-otp, or null when
  // the hospital has OTP disabled — the helpers in publicBooking.ts omit
  // the Authorization header entirely when token is null.
  const runBookingFlow = async (token: string | null) => {
    if (!hospital_id || !selectedDoctor || !selectedAvailabilityId) return;
    const session = availabilities.find(
      (a) => a.availability_id === selectedAvailabilityId
    );
    if (!session) return;

    const fullPhone = `${countryCode}${phone}`;
    setSubmitting(true);
    try {
      // Step 1 — resolve patient_id.
      // - OTP-disabled hospitals: patientId may already be set by the
      //   pre-form debounced autofill search. Use it if so.
      // - OTP-required hospitals: patientId is null here — search authed,
      //   then create if no match.
      let pid: string | null = patientId;
      if (!pid) {
        try {
          const existing = await searchPublicPatient(
            fullPhone,
            hospital_id,
            token
          );
          if (existing?.patient_id) pid = existing.patient_id;
        } catch (searchErr: any) {
          // 403 disabled means the hospital flipped the kill-switch — must
          // not be swallowed; bubble to the outer catch so it can render
          // the unavailable screen.
          if (searchErr?.response?.status === 403) throw searchErr;
          // Otherwise: search miss / 404 is fine — fall through to create.
        }
      }

      if (!pid) {
        const created = await createPublicPatient(
          {
            hospital_id,
            full_name: fullName.trim(),
            contact_number: fullPhone,
            gender: gender ?? undefined,
            date_of_birth:
              Number(age) > 0
                ? moment().subtract(Number(age), "years").format("YYYY-MM-DD")
                : null,
            // Backend does strict `=== true`. Send the literal boolean only
            // when the user actively ticked the box — omit the field
            // otherwise so the row's marketing_consent stays false (and
            // marketing_consent_at stays null) per DPDP/GDPR convention.
            ...(marketingConsent ? { marketing_consent: true } : {}),
          },
          token
        );
        pid = created?.patient_id || null;
      }
      if (!pid) {
        toast.error("Could not create patient. Please try again.");
        return;
      }

      // Step 2 — build booking payload (admin's shape — see memory note
      // `project_express_validator_optional.md` for the queue-type contract).
      const sessionStart = formatTime12Compact(
        session.session_start_time || session.start_time || ""
      );
      const sessionEnd = formatTime12Compact(
        session.session_end_time || session.end_time || ""
      );

      const queueType = session.queue_type || "Slot";
      const perQueue: Record<string, string | number> = {};
      if (queueType === "Slot" || queueType === "Slot_Token") {
        if (selectedSlot?.start && selectedSlot?.end) {
          perQueue.booked_slot_start = `${selectedSlot.start}:00`;
          perQueue.booked_slot_end = `${selectedSlot.end}:00`;
        }
        if (
          queueType === "Slot_Token" &&
          typeof selectedToken?.token_number === "number"
        ) {
          perQueue.token_number = selectedToken.token_number;
        }
      } else if (queueType === "Token") {
        perQueue.booked_slot_start = sessionStart;
        perQueue.booked_slot_end = sessionEnd;
        if (typeof selectedToken?.token_number === "number") {
          perQueue.token_number = selectedToken.token_number;
        }
      } else if (queueType === "Session") {
        perQueue.booked_slot_start = sessionStart;
        perQueue.booked_slot_end = sessionEnd;
      }

      // Hardcoded fields that differ from admin's AppointmentBooking:
      //   - booking_mode: always "booking" (no walk-in path on the public
      //     widget — see project_public_booking_otp_flag memory).
      //   - status: always 0 (pending). Admin uses 1 for walk-ins because
      //     the patient is already physically present; here they aren't.
      //   - visit_type_id: 1 for New, 2 for Follow Up — matches admin's
      //     mapping at AppointmentBooking.tsx:~1298.
      const result: any = await createPublicBooking(
        {
          hospital_id,
          doctor_id: selectedDoctor.doctor_id,
          availability_id: selectedAvailabilityId,
          patient_id: pid,
          booked_date: appointmentDate,
          booking_mode: "booking",
          booking_type: "physical",
          booking_lang: "en",
          is_confirmed: true,
          symptoms: "",
          comments: comments.trim(),
          queue_type: queueType,
          status: 0,
          visit_type_id: visitType === "Follow Up" ? 2 : 1,
          booking_session_start_time: sessionStart,
          booking_session_end_time: sessionEnd,
          booking_time: moment().format("YYYY-MM-DD HH:mm:ss.SSS"),
          ...perQueue,
        },
        token
      );

      const ref =
        result?.booking_id ||
        result?.id ||
        result?.result?.booking_id ||
        "";
      toast.success(
        ref ? `Booking confirmed — Ref: ${ref}` : "Booking confirmed."
      );
      resetForm();
    } catch (err: any) {
      const status = err?.response?.status;
      const rawError = err?.response?.data?.error;
      const errMsg = typeof rawError === "string" ? rawError : "";

      // 403 = hospital flipped the public-booking kill-switch mid-session.
      // Drop the booking_token and render the unavailable screen — same UI
      // as if the flag had been off when the widget loaded.
      if (status === 403 && /disabled/i.test(errMsg)) {
        setBookingToken(null);
        setForcedDisabled(true);
        return;
      }

      // 429 variants — backend disambiguates between hospital-cap, phone-cap
      // and the generic IP/phone burst limit via the error string.
      //   - hospital / phone caps are session-fatal for today: clear the
      //     token, reset to start, tell the patient to try tomorrow / call.
      //   - generic burst limit ("Too many requests") just needs a wait;
      //     don't reset the form so the patient can retry without re-typing.
      if (status === 429) {
        const phone = formatHospitalPhone(branding?.contact_number);
        if (/for this hospital/i.test(errMsg)) {
          setBookingToken(null);
          toast.error(
            phone
              ? `This hospital is full for today. Try again tomorrow or call them at ${phone}.`
              : "This hospital is full for today. Try again tomorrow or call them."
          );
          resetForm();
        } else if (/for this phone number/i.test(errMsg)) {
          setBookingToken(null);
          toast.error(
            "You've reached today's booking limit for this number. Try tomorrow."
          );
          resetForm();
        } else {
          // Generic IP/phone burst limit — server returns either a
          // 1-minute or 10-minute hint in the error string.
          const tenMin = /10\s*minutes?/i.test(errMsg);
          toast.error(
            tenMin
              ? "Slow down — try again in 10 minutes."
              : "Slow down — try again in a minute."
          );
        }
        return;
      }

      // 400 with the advance-days message is a fallback — the date picker
      // should normally clamp to public_max_advance_days, but if the flag
      // was missing or stale, the backend will reject and we surface the
      // friendlier wording here.
      if (status === 400 && /in advance/i.test(errMsg)) {
        const n = flags?.public_max_advance_days;
        toast.error(
          n != null
            ? `Please pick a date within the next ${n} days.`
            : "Please pick a date the hospital is currently accepting bookings for."
        );
        return;
      }

      // 409 = idempotency replay. Same (patient_id, availability_id,
      // appointment_date) was submitted within the backend's 60s dedup
      // window — could be a double-click or a retry after a flake. The
      // server returns the original booking row in the error body, so
      // we surface it as a success instead of a failure.
      if (status === 409) {
        const dup =
          err?.response?.data?.result?.booking_id ||
          err?.response?.data?.booking_id ||
          "";
        toast.success(
          dup ? `Booking confirmed — Ref: ${dup}` : "Booking confirmed."
        );
        resetForm();
        return;
      }
      // 401 on a gated call after a successful verify means the booking_token
      // (10-min TTL) has expired or been invalidated. Drop it and send the
      // user back through Phase 1; the canonical spec wants the phone field
      // to stay filled, which it does because we only clear bookingToken.
      if (status === 401 && otpRequired) {
        setBookingToken(null);
        setOtpOpen(true);
        toast.error("Session expired. Please verify your number again.");
        return;
      }
      const msg =
        err?.response?.data?.error?.[0]?.msg ||
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  // Called by OtpModal when /verify-otp returns 200. Park the token and
  // immediately kick off the booking flow (the OTP modal opens *after*
  // the form is already fully validated, so we don't need any further
  // user input — go straight to Phase 2/3).
  const handleOtpVerified = (token: string) => {
    setBookingToken(token);
    setOtpOpen(false);
    runBookingFlow(token);
  };

  // Pre-form patient search runs ONLY when OTP is disabled for this hospital.
  // For OTP-required hospitals, /patient/search is gated and would 401 — the
  // dedup still happens, but silently inside runBookingFlow after verify.
  useEffect(() => {
    if (otpRequired) return;
    if (!hospital_id) return;
    if (countryCode !== "91" || phone.length !== 10) {
      setPatientId(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      const fullPhone = `${countryCode}${phone}`;
      try {
        const patient = await searchPublicPatient(fullPhone, hospital_id);
        if (cancelled) return;
        if (!patient) {
          setPatientId(null);
          return;
        }
        setPatientId(patient.patient_id);
        if (patient.full_name) setFullName(patient.full_name);
        if (patient.gender === "Male" || patient.gender === "Female") {
          setGender(patient.gender);
        }
        if (patient.date_of_birth) {
          const yrs = moment().diff(moment(patient.date_of_birth), "years");
          if (yrs > 0 && yrs < 130) setAge(String(yrs));
        }
      } catch (e: any) {
        if (cancelled) return;
        // 403 = hospital flipped the kill-switch mid-typing. Surface the
        // unavailable screen instead of silently swallowing.
        if (e?.response?.status === 403) {
          setForcedDisabled(true);
          return;
        }
        setPatientId(null);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [otpRequired, hospital_id, countryCode, phone]);

  // ── Data-fetching effects ────────────────────────────────────────
  // Three-stage cascade:
  //   1. hospital_id → branding + flags  (sets up the page chrome)
  //   2. hospital_id + date → online doctors for that date
  //   3. mapping_id → availabilities (sessions) for the selected doctor
  // Each stage uses the cancelled-flag pattern so that a rapid date or
  // doctor change doesn't race in a stale response.

  // 1. on hospital_id load: parallel fetch hospital + feature flags
  useEffect(() => {
    if (!hospital_id) return;
    let cancelled = false;
    setInitialLoading(true);
    setInitialError(null);

    Promise.all([
      getPublicHospital(hospital_id).catch(() => null),
      getPublicFeatureFlags(hospital_id).catch(() => null),
    ])
      .then(([brand, ff]) => {
        if (cancelled) return;
        if (!brand) {
          setInitialError(
            "We couldn't find this hospital. Please check your booking link."
          );
        } else {
          setBranding(brand);
          setFlags(ff);
        }
      })
      .finally(() => {
        if (!cancelled) setInitialLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hospital_id]);

  // 2. on date change (or hospital_id ready): fetch online doctors for the date
  useEffect(() => {
    if (!hospital_id || !appointmentDate) return;
    let cancelled = false;
    setDoctorsLoading(true);
    setMappingId(null);
    setAvailabilities([]);
    setSelectedAvailabilityId(null);

    getPublicOnlineDoctors(hospital_id, appointmentDate)
      .then((list) => {
        if (cancelled) return;
        setDoctors(list);
        if (list.length > 0) {
          setMappingId(list[0].mapping_id);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (isKillSwitchError(err)) {
          setForcedDisabled(true);
          return;
        }
        setDoctors([]);
      })
      .finally(() => {
        if (!cancelled) setDoctorsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hospital_id, appointmentDate]);

  // 3. on doctor select: fetch sessions for mapping_id
  useEffect(() => {
    if (!mappingId) {
      setAvailabilities([]);
      setSelectedAvailabilityId(null);
      return;
    }
    let cancelled = false;
    setAvailabilityLoading(true);
    setSelectedAvailabilityId(null);

    getPublicDoctorAvailability(mappingId)
      .then((list) => {
        if (cancelled) return;
        setAvailabilities(list);
      })
      .catch((err) => {
        if (cancelled) return;
        if (isKillSwitchError(err)) {
          setForcedDisabled(true);
          return;
        }
        setAvailabilities([]);
      })
      .finally(() => {
        if (!cancelled) setAvailabilityLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [mappingId]);

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CenteredCard>
          <p className="text-queueText">Loading…</p>
        </CenteredCard>
      </div>
    );
  }

  if (initialError || !branding) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CenteredCard>
          <h2 className="text-lg font-semibold text-modalText mb-1">
            Hospital not found
          </h2>
          <p className="text-sm text-queueText">
            {initialError || "Please check your booking link."}
          </p>
        </CenteredCard>
      </div>
    );
  }

  // Master kill-switch — render the unavailable screen and stop. Either the
  // initial flag fetch returned `public_booking_enabled: false`, or a later
  // call returned 403 (the hospital flipped the switch mid-session).
  if (publicDisabled) {
    return <UnavailableScreen branding={branding} />;
  }

  // Follow Up is opt-in per hospital — only show the toggle when the
  // hospital uses PRN-based follow-up tracking (matches admin behavior).
  const showFollowUp = flags?.prn_collection === true;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 md:bg-gradient-to-b md:from-[#EEF3FF] md:via-white md:to-[#F4F7FE]">
      <main className="flex-1 max-w-5xl lg:max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 md:py-5 lg:py-6">
        <PageTitle hospitalName={branding.hospital_name} />

        {/* Standalone hospital identity strip — mirrors admin's page-level
            header. Subtle elevation on md+ to align with the form cards
            below. */}
        <CardHeader branding={branding} />

        {/*
          Single unified surface. Both sections live inside one card so
          the form reads as one connected flow rather than two floating
          islands. A vertical divider on md+ separates the two columns;
          on mobile they stack with a horizontal divider between. A
          brand-color gradient strip across the top of the card anchors
          the surface to the rest of the page chrome.
        */}
        <div className="bg-white rounded-2xl border border-doctorsBorder mt-4 md:mt-3 md:shadow-[0_8px_32px_-16px_rgba(15,60,190,0.2)] overflow-hidden">
          <div className="h-0.5 md:h-1 bg-gradient-to-r from-[#0F3CBE] via-[#3D5AE0] to-[#0F3CBE]" />

          <div className="grid grid-cols-1 md:grid-cols-2 md:divide-x divide-y md:divide-y-0 divide-doctorsBorder">
            {/* ---------- LEFT: Patient Information ---------- */}
            <div className="flex flex-col p-5 md:p-5 lg:p-6">
              <SectionHeader
                eyebrow="Step 1"
                title="Patient Information"
                subtitle="Tell us who's booking the appointment."
              />

            {/* Phone number (drives OTP target + patient lookup). The country
                code is required by the backend's E.164-without-+ contract.
                Helper text below the input warns the user that OTP arrives
                on WhatsApp — they need to enter a number that has WhatsApp
                installed, or they'll never see the code. */}
            <Field label="Phone Number" required>
              <div className="flex gap-2">
                <PhoneNumberSelect
                  value={countryCode}
                  setValue={setCountryCode}
                />
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="Enter phone number"
                  value={phone}
                  onChange={(e) =>
                    // maxLength catches typing in most browsers; the
                    // slice catches paste / IME / autofill that can
                    // bypass the attribute.
                    setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))
                  }
                  className="flex-1 min-w-0 border border-doctorsBorder rounded-lg px-3.5 py-2.5 md:py-2 text-sm md:text-[15px] focus:outline-none focus:border-[#0F3CBE]"
                />
              </div>
              <p className="mt-1.5 flex items-center gap-1.5 text-[11px] md:text-xs text-queueText">
                <FaWhatsapp className="w-3 h-3 text-[#1FAD63] flex-shrink-0" />
                OTP will be sent on WhatsApp — please enter your WhatsApp number.
              </p>
            </Field>

            <Field label="Full Name" required>
              <input
                type="text"
                placeholder="Enter full name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full border border-doctorsBorder rounded-lg px-3.5 py-2.5 md:py-2 text-sm md:text-[15px] focus:outline-none focus:border-[#0F3CBE]"
              />
            </Field>

            {/* Age + Gender share a row. Age is a narrow numeric input; the
                gender pills take the rest of the width as a 2-pill segmented
                control. */}
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-5 sm:col-span-4">
                <Field label="Age" required>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={120}
                    placeholder="Age"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    className="w-full border border-doctorsBorder rounded-lg px-3.5 py-2.5 md:py-2 text-sm md:text-[15px] focus:outline-none focus:border-[#0F3CBE]"
                  />
                </Field>
              </div>
              <div className="col-span-7 sm:col-span-8">
                <Field label="Gender" required>
                  <div className="grid grid-cols-2 gap-3 mt-1">
                    <PillButton
                      selected={gender === "Male"}
                      onClick={() => setGender("Male")}
                      label="Male"
                    />
                    <PillButton
                      selected={gender === "Female"}
                      onClick={() => setGender("Female")}
                      label="Female"
                    />
                  </div>
                </Field>
              </div>
            </div>

            {/* Visit Type — New vs Follow Up. Drives visit_type_id in the
                final booking payload (1 = New, 2 = Follow Up). Follow Up is
                only shown when the hospital tracks PRN-based revisits. Lives
                in the patient column (below Age) since it's a property of
                the patient/visit, not of the slot. */}
            <Field label="Visit Type" required>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <PillButton
                  selected={visitType === "New"}
                  onClick={() => setVisitType("New")}
                  label="New"
                />
                {showFollowUp && (
                  <PillButton
                    selected={visitType === "Follow Up"}
                    onClick={() => setVisitType("Follow Up")}
                    label="Follow Up"
                  />
                )}
              </div>
            </Field>
          </div>

            {/* ---------- RIGHT: Appointment Details ---------- */}
            <div className="flex flex-col p-5 md:p-5 lg:p-6">
              <SectionHeader
                eyebrow="Step 2"
                title="Appointment Details"
                subtitle="Pick your doctor, date and slot."
              />

            <Field label="Choose Doctor" required>
              <DoctorPickerTrigger
                doctor={selectedDoctor}
                loading={doctorsLoading}
                empty={doctors.length === 0}
                onClick={() => setDoctorPickerOpen(true)}
              />
            </Field>

            {/* Date picker and session selector sit side-by-side on md+.
                Session list is filtered by date (see sessionsForDate memo
                — Sun=1..Sat=7 indexing, project_dow_numbering memory). */}
            <Field label="Appointment Date" required>
              <div className="flex gap-2">
                <input
                  type="date"
                  min={todayStr()}
                  max={maxAppointmentDate}
                  value={appointmentDate}
                  onChange={(e) => setAppointmentDate(e.target.value)}
                  className="flex-1 min-w-0 border border-doctorsBorder rounded-lg px-3.5 py-2.5 md:py-2 text-sm md:text-[15px] focus:outline-none focus:border-[#0F3CBE]"
                />
                <select
                  value={selectedAvailabilityId || ""}
                  onChange={(e) =>
                    setSelectedAvailabilityId(e.target.value || null)
                  }
                  disabled={
                    !selectedDoctor ||
                    availabilityLoading ||
                    sessionsForDate.length === 0
                  }
                  className="flex-1 min-w-0 border border-doctorsBorder rounded-lg px-3.5 py-2.5 md:py-2 text-sm md:text-[15px] focus:outline-none focus:border-[#0F3CBE] bg-white disabled:bg-gray-50 disabled:text-queueText"
                >
                  {!selectedDoctor && (
                    <option value="">Select a doctor first</option>
                  )}
                  {selectedDoctor && availabilityLoading && (
                    <option value="">Loading sessions…</option>
                  )}
                  {selectedDoctor &&
                    !availabilityLoading &&
                    sessionsForDate.length === 0 && (
                      <option value="">No session for this day</option>
                    )}
                  {sessionsForDate.map((a) => {
                    const start =
                      a.session_start_time || a.start_time || "";
                    const end = a.session_end_time || a.end_time || "";
                    const label =
                      start || end
                        ? `${formatTime(start)} - ${formatTime(end)}`
                        : "Session";
                    return (
                      <option
                        key={a.availability_id}
                        value={a.availability_id}
                      >
                        {label}
                        {a.queue_type ? ` (${a.queue_type})` : ""}
                      </option>
                    );
                  })}
                </select>
              </div>
            </Field>

            {/* Slot vs Token vs Session rendering — branches on the chosen
                session's queue_type. See the effect that fetches slots/tokens
                above for the underlying API calls. */}
            {slotTokenKind === "slots" && (
              <Field
                label={
                  slotTokenLoading ? "Loading slots…" : "Choose slot timing"
                }
                required={!slotTokenLoading}
              >
                <SlotGrid
                  slots={slots}
                  selected={selectedSlot}
                  onSelect={setSelectedSlot}
                  loading={slotTokenLoading}
                />
              </Field>
            )}

            {slotTokenKind === "tokens" && (
              <Field
                label={slotTokenLoading ? "Loading tokens…" : "Choose Token"}
                required={!slotTokenLoading}
              >
                <TokenGrid
                  tokens={tokens}
                  selected={selectedToken}
                  onSelect={setSelectedToken}
                  loading={slotTokenLoading}
                />
              </Field>
            )}

            {slotTokenKind === "session" && (
              <p className="text-xs text-queueText mt-3">
                Session booking — no slot or token selection needed.
              </p>
            )}

            {/* Comments — free-form, optional. Mirrors admin's textarea
                placement at the tail of the appointment details column. */}
            <Field label="Comments">
              <textarea
                placeholder="Anything the doctor should know? (optional)"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={2}
                className="w-full border border-doctorsBorder rounded-lg px-3.5 py-2.5 md:py-2 text-sm md:text-[15px] focus:outline-none focus:border-[#0F3CBE] resize-y min-h-[56px] md:min-h-[52px]"
              />
            </Field>
            </div>
          </div>
        </div>

        {/* Action bar — checkbox on the left, buttons on the right (mobile
            stacks). Compressed on md+ to keep the whole layout inside one
            viewport; mobile keeps generous tap targets since scroll is fine
            there. The marketing-consent box defaults unticked (DPDP/GDPR);
            it's optional and does NOT gate Submit. */}
        <div className="mt-4 md:mt-3 flex flex-col sm:flex-row gap-3 sm:gap-4 sm:justify-between items-stretch sm:items-center md:bg-white md:rounded-xl md:border md:border-doctorsBorder md:px-4 md:py-2.5 md:shadow-[0_4px_20px_-12px_rgba(15,60,190,0.18)]">
          <label className="flex items-start gap-2 text-xs md:text-[12px] text-queueText cursor-pointer select-none sm:max-w-[60%]">
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-doctorsBorder accent-[#0F3CBE] cursor-pointer flex-shrink-0"
            />
            <span className="leading-snug">
              Send me updates, offers, and health tips from this hospital on WhatsApp.
            </span>
          </label>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <button
              type="button"
              onClick={resetForm}
              className="px-5 py-2.5 md:py-2 rounded-lg border border-[#0F3CBE] text-[#0F3CBE] bg-white font-semibold text-sm md:text-[15px] hover:bg-[#EAF0FF] transition-colors"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full sm:w-auto sm:min-w-[200px] px-5 py-2.5 md:py-2 rounded-lg bg-[#0F3CBE] text-white font-semibold text-sm md:text-[15px] hover:bg-[#0a2f99] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed shadow-[0_8px_24px_-12px_rgba(15,60,190,0.5)]"
            >
              {submitting ? "Booking…" : "Book Appointment"}
            </button>
          </div>
        </div>
      </main>

      <DoctorPickerModal
        open={doctorPickerOpen}
        onClose={() => {
          setDoctorPickerOpen(false);
          setDoctorSearch("");
        }}
        doctors={doctors}
        selectedMappingId={mappingId}
        search={doctorSearch}
        setSearch={setDoctorSearch}
        onPick={(d) => {
          setMappingId(d.mapping_id);
          setDoctorPickerOpen(false);
          setDoctorSearch("");
        }}
      />


      <OtpModal
        open={otpOpen}
        phone={`${countryCode}${phone}`}
        countryCode={countryCode}
        nationalNumber={phone}
        hospitalId={hospital_id || ""}
        hospitalPhone={formatHospitalPhone(branding?.contact_number)}
        onClose={() => setOtpOpen(false)}
        onVerified={handleOtpVerified}
        onSkip={() => {
          // Stale flag — backend says OTP isn't enabled. Drop the modal
          // and run the booking flow without a token; the gated endpoints
          // are permissive when the hospital has the flag off.
          setOtpOpen(false);
          runBookingFlow(null);
        }}
        onPublicDisabled={() => {
          // Hospital flipped the kill-switch between widget load and OTP
          // open. Close the modal, drop the token, and flip into the
          // unavailable screen (parent re-renders).
          setOtpOpen(false);
          setBookingToken(null);
          setForcedDisabled(true);
        }}
      />
    </div>
  );
};

const overflowModalStyles = {
  overlay: {
    backgroundColor: "rgba(2, 11, 23, 0.45)",
    zIndex: 999,
  },
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    transform: "translate(-50%, -50%)",
    padding: 0,
    border: "none",
    borderRadius: 12,
    minWidth: "min(92vw, 560px)",
    maxWidth: "min(92vw, 560px)",
    width: "min(92vw, 560px)",
    maxHeight: "85vh",
    overflow: "hidden",
  },
} as const;

const VISIBLE_LIMIT = 4;

const slotChipClass = (selected: boolean, disabled: boolean) =>
  [
    "px-3 py-1.5 md:py-1.5 lg:px-3.5 lg:py-2 rounded-lg border text-sm lg:text-[15px] transition-colors whitespace-nowrap",
    disabled ? "bg-gray-100 text-gray-400 border-doctorsBorder cursor-not-allowed" : "",
    selected
      ? "bg-[#0F3CBE] text-white border-[#0F3CBE]"
      : !disabled
      ? "bg-white text-modalText border-doctorsBorder hover:border-[#0F3CBE]"
      : "",
  ].join(" ");

const moreChipClass =
  "px-3 py-1.5 md:py-1.5 lg:px-3.5 lg:py-2 rounded-lg border-2 bg-[#0F3CBE] text-white border-[#0F3CBE] text-sm lg:text-[15px] flex items-center gap-1 whitespace-nowrap";

const SlotGrid: React.FC<{
  slots: PublicSlot[];
  selected: PublicSlot | null;
  onSelect: (s: PublicSlot) => void;
  loading: boolean;
}> = ({ slots, selected, onSelect, loading }) => {
  const [open, setOpen] = useState(false);
  if (loading) {
    return <p className="text-sm text-queueText">Loading slots…</p>;
  }
  if (!slots.length) {
    return <p className="text-sm text-queueText">No slots available.</p>;
  }
  const visible = slots.slice(0, VISIBLE_LIMIT);
  const hasMore = slots.length > VISIBLE_LIMIT;
  return (
    <>
      <div className="flex flex-wrap gap-2">
        {visible.map((s, i) => {
          const isSelected =
            selected?.start === s.start && selected?.end === s.end;
          const disabled = s.available === false;
          return (
            <button
              key={`${s.start}-${i}`}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(s)}
              className={slotChipClass(isSelected, disabled)}
            >
              {formatTime(s.start)} - {formatTime(s.end)}
            </button>
          );
        })}
        {hasMore && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={moreChipClass}
          >
            More slots <FiChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
      <Modal
        isOpen={open}
        onRequestClose={() => setOpen(false)}
        style={overflowModalStyles}
        ariaHideApp={false}
      >
        <div className="flex flex-col max-h-[85vh]">
          <div className="px-5 py-3 flex items-center justify-between border-b border-sbBorder">
            <p className="text-modalText font-semibold">Choose slot timing</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-queueText hover:text-modalText"
              aria-label="Close"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
          <div className="overflow-y-auto p-5">
            <div className="flex flex-wrap gap-2">
              {slots.map((s, i) => {
                const isSelected =
                  selected?.start === s.start && selected?.end === s.end;
                const disabled = s.available === false;
                return (
                  <button
                    key={`all-${s.start}-${i}`}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      onSelect(s);
                      setOpen(false);
                    }}
                    className={slotChipClass(isSelected, disabled)}
                  >
                    {formatTime(s.start)} - {formatTime(s.end)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

const TokenGrid: React.FC<{
  tokens: PublicToken[];
  selected: PublicToken | null;
  onSelect: (t: PublicToken) => void;
  loading: boolean;
}> = ({ tokens, selected, onSelect, loading }) => {
  const [open, setOpen] = useState(false);
  if (loading) {
    return <p className="text-sm text-queueText">Loading tokens…</p>;
  }
  if (!tokens.length) {
    return <p className="text-sm text-queueText">No tokens available.</p>;
  }
  const visible = tokens.slice(0, VISIBLE_LIMIT);
  const hasMore = tokens.length > VISIBLE_LIMIT;
  return (
    <>
      <div className="flex flex-wrap gap-2">
        {visible.map((t, i) => {
          const isSelected = selected?.token_number === t.token_number;
          const disabled = t.available === false;
          return (
            <button
              key={`token-${t.token_number}-${i}`}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(t)}
              className={slotChipClass(isSelected, disabled)}
            >
              Token {t.token_number}
            </button>
          );
        })}
        {hasMore && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={moreChipClass}
          >
            More tokens <FiChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
      <Modal
        isOpen={open}
        onRequestClose={() => setOpen(false)}
        style={overflowModalStyles}
        ariaHideApp={false}
      >
        <div className="flex flex-col max-h-[85vh]">
          <div className="px-5 py-3 flex items-center justify-between border-b border-sbBorder">
            <p className="text-modalText font-semibold">Choose Token</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-queueText hover:text-modalText"
              aria-label="Close"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
          <div className="overflow-y-auto p-5">
            <div className="flex flex-wrap gap-2">
              {tokens.map((t, i) => {
                const isSelected = selected?.token_number === t.token_number;
                const disabled = t.available === false;
                return (
                  <button
                    key={`all-token-${t.token_number}-${i}`}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      onSelect(t);
                      setOpen(false);
                    }}
                    className={slotChipClass(isSelected, disabled)}
                  >
                    Token {t.token_number}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

// SectionHeader is the strong heading block for each form section.
// Three layers of hierarchy in a small footprint:
//   1. Eyebrow — small uppercase brand-color tag ("Step 1") that lets
//      the user clock the section count at a glance
//   2. Title — bold modalText, size:xl
//   3. Subtitle — muted queueText, brief one-liner explaining the goal
// Together they create a clear "this is a section" signal without
// needing a divider in the body.
const SectionHeader: React.FC<{
  eyebrow: string;
  title: string;
  subtitle?: string;
}> = ({ eyebrow, title, subtitle }) => (
  <div className="mb-1">
    <div className="flex items-center gap-2">
      <span
        className="h-0.5 w-5 rounded-full bg-gradient-to-r from-[#0F3CBE] to-[#3D5AE0]"
        aria-hidden
      />
      <p className="text-[10px] md:text-[11px] font-semibold uppercase tracking-[0.16em] text-[#0F3CBE]">
        {eyebrow}
      </p>
    </div>
    <h2 className="mt-1.5 text-lg md:text-xl font-bold text-modalText tracking-tight leading-tight">
      {title}
    </h2>
    {subtitle && (
      <p className="mt-0.5 text-xs md:text-[13px] text-queueText leading-snug">
        {subtitle}
      </p>
    )}
  </div>
);

// Field renders an admin-style labelled input row. Label uses the bold
// modalText treatment from AppointmentBooking; required fields get the
// red asterisk shifted up a hair to align with the cap height of the
// label (matches admin's `relative bottom-1` micro-adjustment).
const Field: React.FC<{
  label: string;
  required?: boolean;
  children: React.ReactNode;
}> = ({ label, required, children }) => (
  <div>
    {/* mt-8 was admin's spacing; tightened to mt-5/md:mt-4 here so the
        whole form fits in one viewport on tablet+ desktop without scroll. */}
    <p className="text-modalText text-sm md:text-[15px] mb-1 mt-5 md:mt-4 font-semibold">
      {label}
      {required && (
        <span className="text-red-500 text-sm relative bottom-0.5 font-medium ml-0.5">
          *
        </span>
      )}
    </p>
    <div className="mt-1.5">{children}</div>
  </div>
);

// PillButton matches AppointmentBooking's segmented-control style:
// bordered card that fills its grid cell, switching to solid #0F3CBE on
// select. Hover preview tints unselected pills so the user can feel
// where their tap will land.
const PillButton: React.FC<{
  selected: boolean;
  onClick: () => void;
  label: string;
}> = ({ selected, onClick, label }) => (
  <div
    onClick={onClick}
    className={[
      "px-4 py-2.5 md:py-2 rounded-lg border-2 border-[#E5E5E5] flex flex-col justify-center items-center cursor-pointer w-full transition-colors",
      selected
        ? "bg-[#0F3CBE] text-white border-[#0F3CBE]"
        : "hover:bg-[#0F3CBE] hover:text-white",
    ].join(" ")}
  >
    <p className="font-semibold text-sm md:text-[15px]">{label}</p>
  </div>
);

// Shared modal styles for the OTP modal (was originally defined for the
// doctor picker — name kept for minimal churn since the dimensions
// happen to be a good fit for the OTP modal too).
const doctorPickerModalStyles = {
  overlay: {
    backgroundColor: "rgba(2, 11, 23, 0.45)",
    zIndex: 999,
  },
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    transform: "translate(-50%, -50%)",
    padding: 0,
    border: "none",
    borderRadius: 12,
    minWidth: "min(92vw, 520px)",
    maxWidth: "min(92vw, 520px)",
    width: "min(92vw, 520px)",
    maxHeight: "85vh",
    overflow: "hidden",
  },
} as const;

// DoctorPickerTrigger — the row that sits in the right card under "Choose
// Doctor". Empty state shows hint copy; filled state shows the selected
// doctor's avatar + name + specialization. Tap opens the picker modal.
const DoctorPickerTrigger: React.FC<{
  doctor: PublicDoctor | null;
  loading: boolean;
  empty: boolean;
  onClick: () => void;
}> = ({ doctor, loading, empty, onClick }) => {
  const src =
    doctor?.profile_picture || doctor?.photo_url || doctor?.image_url;
  const [failed, setFailed] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading || empty}
      className="w-full flex items-center gap-2 md:gap-2.5 px-3 py-2 md:px-3.5 md:py-2 rounded-lg border border-doctorsBorder text-left transition-colors hover:border-[#0F3CBE] disabled:opacity-60 disabled:cursor-not-allowed bg-white"
    >
      {doctor ? (
        <>
          {src && !failed ? (
            <img
              src={src}
              alt={doctor.full_name}
              onError={() => setFailed(true)}
              className="w-8 h-8 md:w-9 md:h-9 rounded-full object-cover bg-gray-100 flex-shrink-0"
            />
          ) : (
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-[#0F3CBE] text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">
              <FiUser className="w-3.5 h-3.5" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm md:text-[15px] font-medium text-modalText truncate leading-tight">
              {doctor.full_name}
            </p>
            {doctor.specialization && (
              <p className="text-xs md:text-[13px] text-queueText truncate leading-tight mt-0.5">
                {doctor.specialization}
              </p>
            )}
          </div>
        </>
      ) : (
        <span className="flex-1 text-sm md:text-[15px] text-queueText">
          {loading
            ? "Loading doctors…"
            : empty
            ? "No online doctors for this date"
            : "Choose a doctor"}
        </span>
      )}
      <FiChevronRight className="w-4 h-4 text-queueText flex-shrink-0" />
    </button>
  );
};

// DoctorPickerModal — full doctor list with search. Opens from the trigger
// above. Selecting a doctor sets mappingId and closes the modal.
const DoctorPickerModal: React.FC<{
  open: boolean;
  onClose: () => void;
  doctors: PublicDoctor[];
  selectedMappingId: string | null;
  search: string;
  setSearch: (s: string) => void;
  onPick: (d: PublicDoctor) => void;
}> = ({ open, onClose, doctors, selectedMappingId, search, setSearch, onPick }) => {
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return doctors;
    return doctors.filter(
      (d) =>
        d.full_name?.toLowerCase().includes(q) ||
        d.specialization?.toLowerCase().includes(q)
    );
  }, [doctors, search]);

  return (
    <Modal
      isOpen={open}
      onRequestClose={onClose}
      style={doctorPickerModalStyles}
      ariaHideApp={false}
    >
      <div className="flex flex-col h-full max-h-[85vh]">
        <div className="px-5 py-3 flex items-center justify-between border-b border-sbBorder">
          <p className="text-modalText font-semibold">Choose Doctor</p>
          <button
            type="button"
            onClick={onClose}
            className="text-queueText hover:text-modalText"
            aria-label="Close"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-sbBorder">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-doctorsBorder focus-within:border-[#0F3CBE]">
            <FiSearch className="w-4 h-4 text-queueText flex-shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or specialization"
              className="flex-1 min-w-0 text-sm focus:outline-none"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {filtered.length === 0 ? (
            <p className="text-sm text-queueText text-center py-8">
              No matching doctors.
            </p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {filtered.map((d) => (
                <DoctorCard
                  key={d.mapping_id}
                  doctor={d}
                  selected={selectedMappingId === d.mapping_id}
                  onClick={() => onPick(d)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

const OTP_LENGTH = 6;
const OTP_TTL_SECONDS = 300; // 5 min — matches backend
// Backend re-tuned (2026-05-14): per-phone send-otp cooldown is 45s, not
// 60s. Keep this constant in step or the local Resend button will be
// disabled longer than the backend actually requires. The server is the
// source of truth for the per-IP (8/10min) and per-phone (7/10min) caps —
// we don't pre-throttle, just react to the 429 when one is hit.
const RESEND_COOLDOWN_SECONDS = 45;

const formatMmSs = (totalSeconds: number): string => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

// Mask the phone for display: keep first 2 + last 2 digits, mask the rest.
// e.g. "919876543210" → "+91 98•••••3210"
const maskPhone = (countryCode: string, national: string): string => {
  if (national.length <= 4) return `+${countryCode} ${national}`;
  const first = national.slice(0, 2);
  const last = national.slice(-2);
  const masked = "•".repeat(Math.max(national.length - 4, 0));
  return `+${countryCode} ${first}${masked}${last}`;
};

const OtpModal: React.FC<{
  open: boolean;
  phone: string; // E.164 without +, e.g. "919876543210"
  countryCode: string;
  nationalNumber: string;
  hospitalId: string;
  onClose: () => void;
  onVerified: (bookingToken: string) => void;
  // Invoked when the backend tells us OTP isn't enabled for this hospital
  // (rare — flag fetch should have caught it, but kept for race safety).
  // Parent should close the modal and run the booking flow without a token.
  onSkip: () => void;
  // Invoked when send-otp / verify-otp returns 403 "disabled" — the
  // hospital flipped the master kill-switch mid-session. Parent should
  // close the modal and render the unavailable screen.
  onPublicDisabled: () => void;
  // Pre-formatted (with leading "+") hospital phone — used inline in the
  // OTP daily-ceiling error message so the patient has a fallback path.
  // Empty string when the hospital has no contact_number on file.
  hospitalPhone?: string;
}> = ({
  open,
  phone,
  countryCode,
  nationalNumber,
  hospitalId,
  onClose,
  onVerified,
  onSkip,
  onPublicDisabled,
  hospitalPhone,
}) => {
  const [digits, setDigits] = useState<string[]>(
    () => Array(OTP_LENGTH).fill("")
  );
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Backend collapses lockout-after-5-wrong-attempts into 410 "expired", so
  // the frontend doesn't track per-attempt counters. The only session-level
  // lock we track here is the send-otp 10-minute hard cap (3 sends per
  // phone/IP per 10 min); when tripped, Resend is permanently disabled for
  // the rest of the modal session.
  const [hardCapped, setHardCapped] = useState(false);
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(OTP_TTL_SECONDS);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [expired, setExpired] = useState(false);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const otp = digits.join("");

  const resetState = () => {
    setDigits(Array(OTP_LENGTH).fill(""));
    setError(null);
    setHardCapped(false);
    setOtpSecondsLeft(OTP_TTL_SECONDS);
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    setExpired(false);
  };

  const doSend = async () => {
    if (!phone || !hospitalId) return;
    setSending(true);
    setError(null);
    try {
      await sendBookingOtp({ contact_number: phone, hospital_id: hospitalId });
      // Restart per-OTP timers but don't touch hardCapped — once tripped,
      // it stays for the rest of the modal session.
      setDigits(Array(OTP_LENGTH).fill(""));
      setOtpSecondsLeft(OTP_TTL_SECONDS);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setExpired(false);
      setTimeout(() => inputsRef.current[0]?.focus(), 50);
    } catch (err: any) {
      const status = err?.response?.status;
      const rawError = err?.response?.data?.error;
      const errMsg = typeof rawError === "string" ? rawError : "";
      if (status === 403 && /disabled/i.test(errMsg)) {
        // Hospital flipped the kill-switch between widget load and OTP open.
        // Bubble up — parent closes the modal and renders the unavailable
        // screen.
        onPublicDisabled();
        return;
      } else if (status === 429) {
        // Backend overloads 429 with three distinct meanings — disambiguate
        // by parsing the error message:
        //   - "Daily OTP send limit reached" → hospital-level OTP credit
        //     ceiling tripped → permanent session lock + a clear message
        //     pointing the patient at the hospital phone.
        //   - "Please try again in 10 minutes" → hard cap (server-side
        //     send rate-limit per phone/IP per 10 min) → permanent
        //     session lock.
        //   - other → per-phone send cooldown (45s) → drive the local
        //     cooldown from Retry-After when present.
        if (/daily/i.test(errMsg) && /otp/i.test(errMsg)) {
          setHardCapped(true);
          setError(
            hospitalPhone
              ? `Today's OTP limit reached. Please call the hospital at ${hospitalPhone}.`
              : "Today's OTP limit reached. Please call the hospital."
          );
        } else if (/10\s*minutes?/i.test(errMsg)) {
          setHardCapped(true);
          setError(
            "You've requested too many codes — please wait 10 minutes."
          );
        } else {
          const retryAfterRaw =
            err?.response?.headers?.["retry-after"] ??
            err?.response?.data?.result?.retry_after;
          const retryAfter = Number(retryAfterRaw);
          setResendCooldown(
            Number.isFinite(retryAfter) && retryAfter > 0
              ? Math.ceil(retryAfter)
              : RESEND_COOLDOWN_SECONDS
          );
          setError("Please wait a moment before requesting another code.");
        }
      } else if (status === 400) {
        // Defensive fallback: feature-flag fetch could race with a backend
        // toggle. If the server says OTP isn't enabled for this hospital,
        // the spec is to skip OTP entirely and let the booking flow
        // proceed without a token rather than block the patient.
        if (/not enabled/i.test(errMsg)) {
          onSkip();
          return;
        }
        setError("Couldn't send the code. Please check your phone number.");
      } else {
        // 502 / 500 / network — never echo backend strings, they leak
        // implementation detail ("OTP storage unavailable" etc.). Match
        // the canonical spec wording so the message is predictable.
        setError("Could not send the OTP right now. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  // Fire send-otp once when the modal opens.
  useEffect(() => {
    if (!open) return;
    resetState();
    doSend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // OTP TTL countdown.
  useEffect(() => {
    if (!open || expired) return;
    if (otpSecondsLeft <= 0) {
      setExpired(true);
      return;
    }
    const t = setTimeout(() => setOtpSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [open, otpSecondsLeft, expired]);

  // Resend cooldown.
  useEffect(() => {
    if (!open || resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [open, resendCooldown]);

  const handleDigitChange = (idx: number, value: string) => {
    const clean = value.replace(/\D/g, "");
    if (!clean) {
      const next = [...digits];
      next[idx] = "";
      setDigits(next);
      return;
    }
    // Support paste: if more than 1 char comes in, distribute across boxes.
    if (clean.length > 1) {
      const next = [...digits];
      for (let i = 0; i < clean.length && idx + i < OTP_LENGTH; i++) {
        next[idx + i] = clean[i];
      }
      setDigits(next);
      const lastFilled = Math.min(idx + clean.length, OTP_LENGTH) - 1;
      inputsRef.current[lastFilled]?.focus();
      return;
    }
    const next = [...digits];
    next[idx] = clean[0];
    setDigits(next);
    if (idx < OTP_LENGTH - 1) inputsRef.current[idx + 1]?.focus();
  };

  const handleDigitKeyDown = (
    idx: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && idx > 0) inputsRef.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < OTP_LENGTH - 1)
      inputsRef.current[idx + 1]?.focus();
  };

  const doVerify = async () => {
    if (otp.length !== OTP_LENGTH) {
      setError("Enter the 6-digit code.");
      return;
    }
    if (expired) {
      setError("Code expired. Tap Resend.");
      return;
    }
    setVerifying(true);
    setError(null);
    try {
      const res = await verifyBookingOtp({
        contact_number: phone,
        hospital_id: hospitalId,
        otp,
      });
      onVerified(res.booking_token);
    } catch (err: any) {
      const status = err?.response?.status;
      const rawError = err?.response?.data?.error;
      const errMsg = typeof rawError === "string" ? rawError : "";
      if (status === 403 && /disabled/i.test(errMsg)) {
        // Same race as in doSend — kill-switch flipped after the OTP was
        // sent but before verify completed. Bubble up to the parent.
        onPublicDisabled();
        return;
      }
      if (status === 410) {
        // 410 covers BOTH true TTL expiry AND post-lockout (5+ wrong tries),
        // collapsed by the backend to avoid leaking the lockout signal. The
        // canonical spec says: toast + re-trigger send-otp flow. So we
        // surface a toast and auto-fire a fresh send. If the modal is
        // already hard-capped (server's per-IP/per-phone send cap was
        // tripped earlier), fall back to inline messaging instead.
        if (hardCapped) {
          setExpired(true);
          setError("Code expired. Please close and try again later.");
        } else {
          toast.error("Code expired — sending a new one.");
          doSend();
        }
      } else if (status === 401) {
        setDigits(Array(OTP_LENGTH).fill(""));
        inputsRef.current[0]?.focus();
        setError("Wrong code. Try again.");
      } else if (status === 429) {
        setError("Too many attempts. Please try again later.");
      } else {
        setError("We couldn't verify the code. Please try again.");
      }
    } finally {
      setVerifying(false);
    }
  };

  // Auto-submit when all 6 digits are typed.
  useEffect(() => {
    if (otp.length === OTP_LENGTH && !verifying && !expired && !hardCapped) {
      doVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  return (
    <Modal
      isOpen={open}
      onRequestClose={onClose}
      style={doctorPickerModalStyles}
      ariaHideApp={false}
    >
      <div className="flex flex-col max-h-[85vh]">
        <div className="px-5 lg:px-6 py-4 flex items-center justify-between border-b border-sbBorder">
          <p className="text-modalText font-semibold text-base lg:text-lg">
            Verify your phone
          </p>
          <button
            type="button"
            onClick={onClose}
            className="text-queueText hover:text-modalText"
            aria-label="Close"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 lg:p-6 space-y-5">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E7F8EF] text-[#1FAD63] text-xs font-semibold mb-2">
              <FaWhatsapp className="w-3.5 h-3.5" />
              Sent on WhatsApp
            </div>
            <p className="text-sm text-queueText">
              We sent a 6-digit code on WhatsApp to
            </p>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-modalText font-semibold text-base">
                {maskPhone(countryCode, nationalNumber)}
              </p>
              <button
                type="button"
                onClick={onClose}
                className="text-[#0F3CBE] text-xs font-medium underline-offset-2 hover:underline"
              >
                Edit
              </button>
            </div>
          </div>

          <div>
            <p className="text-sm text-modalText text-center mb-3">
              Enter the code below to confirm your appointment booking.
            </p>
            <div className="flex gap-2 sm:gap-3 justify-center">
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    inputsRef.current[i] = el;
                  }}
                  type="tel"
                  inputMode="numeric"
                  maxLength={1}
                  value={d}
                  disabled={sending || verifying || expired || hardCapped}
                  onChange={(e) => handleDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleDigitKeyDown(i, e)}
                  onFocus={(e) => e.target.select()}
                  className="w-10 h-12 sm:w-12 sm:h-14 text-center text-lg lg:text-xl font-semibold border border-doctorsBorder rounded-lg focus:outline-none focus:border-[#0F3CBE] disabled:bg-gray-50 disabled:text-queueText"
                />
              ))}
            </div>

            {error && (
              <p className="mt-3 text-sm text-onLeave text-center">
                {error}
              </p>
            )}

            <div className="mt-4 flex items-center justify-between text-xs text-queueText">
              <span>
                {expired ? (
                  <span className="text-onLeave font-medium">Code expired</span>
                ) : (
                  <>Code expires in {formatMmSs(Math.max(otpSecondsLeft, 0))}</>
                )}
              </span>
              <button
                type="button"
                disabled={sending || resendCooldown > 0 || hardCapped}
                onClick={doSend}
                className="text-[#0F3CBE] font-medium disabled:text-queueText disabled:cursor-not-allowed"
              >
                {sending
                  ? "Sending…"
                  : hardCapped
                  ? "Try later"
                  : resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : "Resend OTP"}
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={doVerify}
            disabled={
              verifying ||
              sending ||
              otp.length !== OTP_LENGTH ||
              expired ||
              hardCapped
            }
            className="w-full px-5 py-3 lg:py-3.5 rounded-lg bg-[#0F3CBE] text-white font-semibold text-sm lg:text-base hover:bg-[#0a2f99] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {verifying ? "Verifying…" : "Verify & Book"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const DoctorCard: React.FC<{
  doctor: PublicDoctor;
  selected: boolean;
  onClick: () => void;
}> = ({ doctor, selected, onClick }) => {
  const src = doctor.profile_picture || doctor.photo_url || doctor.image_url;
  const [failed, setFailed] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-left transition-colors",
        selected
          ? "border-[#0F3CBE] bg-[#EAF0FF]"
          : "border-doctorsBorder hover:border-[#0F3CBE]",
      ].join(" ")}
    >
      {src && !failed ? (
        <img
          src={src}
          alt={doctor.full_name}
          onError={() => setFailed(true)}
          className="w-8 h-8 rounded-full object-cover bg-gray-100 flex-shrink-0"
        />
      ) : (
        <div className="w-8 h-8 rounded-full bg-[#0F3CBE] text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">
          <FiUser className="w-3.5 h-3.5" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-modalText truncate leading-tight">
          {doctor.full_name}
        </p>
        {doctor.specialization && (
          <p className="text-xs text-queueText truncate leading-tight">
            {doctor.specialization}
          </p>
        )}
      </div>
    </button>
  );
};

const formatTime = (t: string | undefined): string => {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hour = Number(h);
  if (Number.isNaN(hour)) return t;
  const suffix = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${m ?? "00"} ${suffix}`;
};

// Compact 12-hour format used in the booking_session_start/end_time payload
// fields. Admin sends "12:00PM" / "01:00PM" — zero-padded hour, no space
// before AM/PM. Different from formatTime above (which has the space and is
// for user-facing labels).
const formatTime12Compact = (t: string | undefined): string => {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hour = Number(h);
  if (Number.isNaN(hour)) return t;
  const suffix = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${String(hour12).padStart(2, "0")}:${(m ?? "00").slice(0, 2)}${suffix}`;
};

const CenteredCard: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="flex items-center justify-center px-4 py-12 min-h-[60vh]">
    <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 text-center">
      {children}
    </div>
  </div>
);

// True when an axios error matches the backend's master kill-switch 403.
// Centralised so every server-call catch path can surface the unavailable
// screen consistently — if the hospital flips the switch mid-session, even
// a stray read endpoint should bubble it up instead of degrading silently.
const isKillSwitchError = (err: any): boolean => {
  if (err?.response?.status !== 403) return false;
  const raw = err?.response?.data?.error;
  return typeof raw === "string" && /disabled/i.test(raw);
};

// Pretty-prints a raw contact_number for use in toast strings. The DB column
// holds the digits-only form (e.g. "919876543210"); we add a "+" if missing
// so it reads like a phone number rather than an opaque token. Returns ""
// when there's nothing usable so callers can branch.
const formatHospitalPhone = (raw: string | undefined | null): string => {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  if (!trimmed) return "";
  return trimmed.startsWith("+") ? trimmed : `+${trimmed}`;
};

// Master kill-switch screen — shown when public_booking_enabled is false
// at widget load OR when any later call returns 403 "disabled". No form,
// no OTP, no retry; the only action is to call the hospital.
const UnavailableScreen: React.FC<{ branding: HospitalBranding }> = ({
  branding,
}) => {
  const phoneRaw = branding.contact_number || branding.contact_info?.phone;
  const phone = formatHospitalPhone(phoneRaw);
  const tel = phone ? phone.replace(/\s+/g, "") : "";
  return (
    <div className="min-h-screen bg-gray-50 md:bg-gradient-to-b md:from-[#EEF3FF] md:via-white md:to-[#F4F7FE] flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full bg-white rounded-2xl border border-doctorsBorder shadow-[0_8px_32px_-16px_rgba(15,60,190,0.2)] overflow-hidden">
        <div className="h-0.5 md:h-1 bg-gradient-to-r from-[#0F3CBE] via-[#3D5AE0] to-[#0F3CBE]" />
        <div className="p-6 md:p-8 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-modalText mb-2 leading-tight">
            Online booking is temporarily unavailable
          </h2>
          <p className="text-sm md:text-[15px] text-queueText mb-6">
            {branding.hospital_name
              ? `${branding.hospital_name} isn't accepting online appointments right now. `
              : "This hospital isn't accepting online appointments right now. "}
            Please call the hospital directly or visit in person.
          </p>
          {tel ? (
            <a
              href={`tel:${tel}`}
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-[#0F3CBE] text-white font-semibold text-sm md:text-[15px] hover:bg-[#0a2f99] transition-colors shadow-[0_8px_24px_-12px_rgba(15,60,190,0.5)]"
            >
              Call {phone}
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// Standalone hospital identity strip rendered above the two form cards.
// Mirrors admin AppointmentBooking's page-level header treatment, with
// an extra "Secure online booking" trust badge that's specific to the
// public widget (the patient hasn't logged in, so a trust signal helps).
const CardHeader: React.FC<{ branding: HospitalBranding }> = ({ branding }) => {
  const [logoFailed, setLogoFailed] = React.useState(false);
  const initial = branding.hospital_name?.charAt(0)?.toUpperCase() || "H";
  return (
    <div className="flex items-center justify-between gap-4 mt-2 px-4 sm:px-5 py-2.5 md:py-3 rounded-xl bg-gradient-to-r from-white to-[#F8FAFF] border border-doctorsBorder md:shadow-[0_4px_20px_-12px_rgba(15,60,190,0.18)]">
      <div className="flex items-center gap-3 min-w-0">
        {branding.logo_url && !logoFailed ? (
          <img
            src={branding.logo_url}
            alt={branding.hospital_name}
            onError={() => setLogoFailed(true)}
            className="w-9 h-9 md:w-10 md:h-10 rounded-lg object-cover bg-gray-100 flex-shrink-0"
          />
        ) : (
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-gradient-to-br from-[#0F3CBE] to-[#3D5AE0] text-white font-bold text-base md:text-lg flex items-center justify-center flex-shrink-0">
            {initial}
          </div>
        )}
        <div className="min-w-0">
          <p className="text-modalText font-semibold text-sm md:text-base truncate leading-tight">
            {branding.hospital_name}
          </p>
          {(branding.address || branding.city) && (
            <p className="text-[11px] md:text-xs text-queueText truncate leading-tight mt-0.5">
              {[branding.address, branding.city].filter(Boolean).join(", ")}
            </p>
          )}
        </div>
      </div>
      <div className="hidden sm:flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#EAF0FF] text-[#0F3CBE] text-[11px] md:text-xs font-semibold whitespace-nowrap">
        <span className="w-1.5 h-1.5 rounded-full bg-[#0F3CBE]" />
        Secure online booking
      </div>
    </div>
  );
};

// On md+ we make this header more compact and visually refined so the
// rest of the form has room to fit in a single viewport: smaller h1,
// tighter margins, and a brand-gradient text fill on "appointment" to
// give the heading a finished feel without bloating its size.
const PageTitle: React.FC<{ hospitalName?: string }> = ({ hospitalName }) => (
  <div className="mb-4 md:mb-3 lg:mb-4">
    <div className="flex items-center gap-2 mb-1.5">
      <span
        className="h-1 w-8 lg:w-10 rounded-full bg-gradient-to-r from-[#0F3CBE] to-[#3D5AE0]"
        aria-hidden
      />
      <span className="text-[11px] lg:text-xs font-semibold uppercase tracking-[0.18em] text-[#0F3CBE]">
        Online booking
      </span>
    </div>
    <h1 className="text-2xl sm:text-3xl md:text-2xl lg:text-3xl font-bold text-modalText leading-tight tracking-tight">
      Book your{" "}
      <span className="bg-gradient-to-r from-[#0F3CBE] to-[#3D5AE0] bg-clip-text text-transparent">
        appointment
      </span>
      {hospitalName ? (
        <span className="block text-sm sm:text-base md:text-sm lg:text-base font-medium text-queueText mt-1">
          with {hospitalName}
        </span>
      ) : null}
    </h1>
  </div>
);

export default PublicBooking;
